import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  IconButton,
  Tabs,
  Tab,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Checkbox,
  TextField,
  Alert,
  Tooltip,
  Paper,
  Chip,
  useTheme,
  alpha,
  Divider,
  Stack
} from '@mui/material';
import {
  Print as PrintIcon,
  Close as CloseIcon,
  QrCode2 as QrCodeIcon,
  ViewWeek as BarcodeIcon,
  Add as AddIcon,
  Remove as RemoveIcon,
  Code as CodeIcon,
  ContentCopy as CopyIcon,
  Download as DownloadIcon,
  Tune as TuneIcon,
  InfoOutlined as InfoIcon,
  CheckCircleOutline as CheckIcon
} from '@mui/icons-material';
import Barcode from 'react-barcode';
import { QRCodeSVG } from 'qrcode.react';
import { formatCurrency } from '../../utils/formatters';
import { printerService } from '../../services/printerService';
import { LABEL_PRESETS, generateZplLabel, downloadZplFile } from '../../utils/zplGenerator';
import { useAuth } from '../../context/AuthContextMongo';
import { formatProductQrValue, formatProductCodeValue } from '../../utils/codeResolver';

const LabelPreview = ({ open, onClose, product, products = [] }) => {
  const theme = useTheme();
  const { user } = useAuth();
  const businessName = user?.negocio?.nombre || user?.nombre || 'POSENT';

  // Configuración de impresión
  const [tabIndex, setTabIndex] = useState(0); // 0: Vista previa & Ajustes, 1: ZPL Nativo
  const [sizeKey, setSizeKey] = useState('50x25');
  const [orientation, setOrientation] = useState('horizontal'); // 'horizontal' | 'vertical'
  const [codeType, setCodeType] = useState('barcode'); // 'barcode' | 'qr'
  const [copies, setCopies] = useState(1);
  const [useStockCount, setUseStockCount] = useState(false);
  const [loading, setLoading] = useState(false);
  const [copiedZpl, setCopiedZpl] = useState(false);

  // Opciones de visualización en la etiqueta
  const [options, setOptions] = useState({
    showName: true,
    showPrice: true,
    showCode: true,
    showCategory: false,
    showBusinessName: false,
    businessName: businessName,
  });

  // Determinar qué productos imprimir
  const itemsToPrint = products.length > 0 ? products : (product ? [product] : []);
  const primaryItem = itemsToPrint[0] || {};
  const currentPreset = LABEL_PRESETS[sizeKey] || LABEL_PRESETS['50x25'];

  // Calcular total real de etiquetas
  const totalLabels = itemsToPrint.reduce((acc, item) => {
    const qty = useStockCount ? (parseInt(item.stock || item.stock_quantity) || 1) : copies;
    return acc + qty;
  }, 0);

  // Generar ZPL para vista técnica
  const generatedZpl = generateZplLabel(primaryItem, {
    size: sizeKey,
    copies: copies,
    type: codeType,
    showPrice: options.showPrice,
    showName: options.showName,
    showCode: options.showCode,
    showCategory: options.showCategory,
    showBusinessName: options.showBusinessName,
    businessName: options.businessName || businessName,
  });

  const handlePrint = async () => {
    try {
      setLoading(true);
      await printerService.printLabel(itemsToPrint, {
        size: sizeKey,
        orientation: orientation,
        copies: copies,
        type: codeType,
        showPrice: options.showPrice,
        showName: options.showName,
        showCode: options.showCode,
        showCategory: options.showCategory,
        showBusinessName: options.showBusinessName,
        businessName: options.businessName || businessName,
        useStockCount: useStockCount,
      });
    } catch (error) {
      console.error('Error al imprimir etiquetas:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCopyZpl = () => {
    navigator.clipboard.writeText(generatedZpl);
    setCopiedZpl(true);
    setTimeout(() => setCopiedZpl(false), 2000);
  };

  const handleDownloadZpl = () => {
    const fileName = `etiqueta_${(primaryItem.name || 'producto').replace(/\s+/g, '_').toLowerCase()}_rp425.zpl`;
    downloadZplFile(generatedZpl, fileName);
  };

  if (itemsToPrint.length === 0) return null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          boxShadow: '0 20px 60px rgba(0,0,0,0.25)',
          overflow: 'hidden',
          bgcolor: theme.palette.background.paper,
        }
      }}
    >
      <DialogTitle sx={{
        p: 2.5,
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        borderBottom: `1px solid ${theme.palette.divider}`,
        background: alpha(theme.palette.primary.main, 0.04)
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box sx={{
            p: 1,
            borderRadius: 2,
            bgcolor: alpha(theme.palette.primary.main, 0.1),
            color: theme.palette.primary.main,
            display: 'flex'
          }}>
            <PrintIcon />
          </Box>
          <Box>
            <Typography variant="h6" fontWeight="bold">
              Impresión de Etiquetas RP425
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Optimizado para Rongta RP425 (ZPL 203 DPI) y térmicas estándar
            </Typography>
          </Box>
        </Box>
        <IconButton size="small" onClick={onClose} sx={{ color: 'text.secondary' }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <Box sx={{ borderBottom: 1, borderColor: 'divider', px: 3, bgcolor: alpha(theme.palette.background.default, 0.5) }}>
        <Tabs value={tabIndex} onChange={(_, val) => setTabIndex(val)}>
          <Tab icon={<TuneIcon sx={{ fontSize: 18 }} />} iconPosition="start" label="Ajustes y Vista Previa" />
          <Tab icon={<CodeIcon sx={{ fontSize: 18 }} />} iconPosition="start" label="Código ZPL Directo" />
        </Tabs>
      </Box>

      <DialogContent sx={{ p: 3 }}>
        {tabIndex === 0 && (
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1.2fr 1fr' }, gap: 3 }}>
            {/* Columna Izquierda: Ajustes de Etiqueta */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
              {/* Selector de Tamaño y Orientación */}
              <Box sx={{ display: 'grid', gridTemplateColumns: '1.2fr 1fr', gap: 1.5 }}>
                <FormControl fullWidth size="small">
                  <InputLabel>Tamaño Etiqueta</InputLabel>
                  <Select
                    value={sizeKey}
                    label="Tamaño Etiqueta"
                    onChange={(e) => setSizeKey(e.target.value)}
                  >
                    {Object.entries(LABEL_PRESETS).map(([key, item]) => (
                      <MenuItem key={key} value={key}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
                          <span>{item.name}</span>
                          <Typography variant="caption" color="text.secondary">
                            {item.widthMm}x{item.heightMm} mm
                          </Typography>
                        </Box>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <FormControl fullWidth size="small">
                  <InputLabel>Orientación</InputLabel>
                  <Select
                    value={orientation}
                    label="Orientación"
                    onChange={(e) => setOrientation(e.target.value)}
                  >
                    <MenuItem value="horizontal">Horizontal (50x25)</MenuItem>
                    <MenuItem value="vertical">Vertical (Rotada 90°)</MenuItem>
                  </Select>
                </FormControl>
              </Box>

              {/* Selector de Tipo de Código y Copias */}
              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                <Box>
                  <Typography variant="caption" fontWeight="bold" color="text.secondary" gutterBottom>
                    Tipo de Código:
                  </Typography>
                  <Stack direction="row" spacing={1} sx={{ mt: 0.5 }}>
                    <Button
                      size="small"
                      variant={codeType === 'barcode' ? 'contained' : 'outlined'}
                      startIcon={<BarcodeIcon />}
                      onClick={() => setCodeType('barcode')}
                      fullWidth
                    >
                      Barras
                    </Button>
                    <Button
                      size="small"
                      variant={codeType === 'qr' ? 'contained' : 'outlined'}
                      startIcon={<QrCodeIcon />}
                      onClick={() => setCodeType('qr')}
                      fullWidth
                    >
                      QR
                    </Button>
                  </Stack>
                </Box>

                <Box>
                  <Typography variant="caption" fontWeight="bold" color="text.secondary" gutterBottom>
                    Copias por producto:
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', mt: 0.5, border: 1, borderColor: 'divider', borderRadius: 1.5, height: 36 }}>
                    <IconButton
                      size="small"
                      onClick={() => { setCopies(Math.max(1, copies - 1)); setUseStockCount(false); }}
                      disabled={useStockCount || copies <= 1}
                    >
                      <RemoveIcon fontSize="small" />
                    </IconButton>
                    <Typography sx={{ flex: 1, textAlign: 'center', fontWeight: 'bold' }}>
                      {useStockCount ? 'Auto' : copies}
                    </Typography>
                    <IconButton
                      size="small"
                      onClick={() => { setCopies(copies + 1); setUseStockCount(false); }}
                      disabled={useStockCount}
                    >
                      <AddIcon fontSize="small" />
                    </IconButton>
                  </Box>
                </Box>
              </Box>

              {/* Casilla usar stock */}
              <FormControlLabel
                control={
                  <Checkbox
                    checked={useStockCount}
                    onChange={(e) => setUseStockCount(e.target.checked)}
                    size="small"
                  />
                }
                label={
                  <Typography variant="body2">
                    Imprimir según stock disponible de cada producto
                  </Typography>
                }
              />

              <Divider />

              {/* Elementos a mostrar */}
              <Box>
                <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                  Elementos Visibles en la Etiqueta:
                </Typography>
                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 0.5 }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={options.showName}
                        onChange={(e) => setOptions({ ...options, showName: e.target.checked })}
                        size="small"
                      />
                    }
                    label={<Typography variant="body2">Nombre del producto</Typography>}
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={options.showPrice}
                        onChange={(e) => setOptions({ ...options, showPrice: e.target.checked })}
                        size="small"
                      />
                    }
                    label={<Typography variant="body2">Precio en grande</Typography>}
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={options.showCode}
                        onChange={(e) => setOptions({ ...options, showCode: e.target.checked })}
                        size="small"
                      />
                    }
                    label={<Typography variant="body2">Código / Código de Barras</Typography>}
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={options.showCategory}
                        onChange={(e) => setOptions({ ...options, showCategory: e.target.checked })}
                        size="small"
                      />
                    }
                    label={<Typography variant="body2">Categoría</Typography>}
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={options.showBusinessName}
                        onChange={(e) => setOptions({ ...options, showBusinessName: e.target.checked })}
                        size="small"
                      />
                    }
                    label={<Typography variant="body2">Nombre del negocio</Typography>}
                  />
                </Box>
              </Box>

              {/* Tip de Calibración RP425 */}
              <Alert severity="info" icon={<InfoIcon />} sx={{ py: 0.5, px: 1.5, borderRadius: 2, fontSize: '0.8rem' }}>
                <strong>Tip Rongta RP425:</strong> Si las etiquetas salen desalineadas, mantén presionado el botón <strong>FEED</strong> hasta escuchar <strong>1 pitido</strong> para calibrar el sensor.
              </Alert>

              {/* Tip indispensable para Chrome Print Preview */}
              <Alert severity="warning" sx={{ py: 0.5, px: 1.5, borderRadius: 2, fontSize: '0.78rem' }}>
                <strong>Ajuste en Chrome al imprimir:</strong> Abre <strong>Más ajustes</strong> y <strong>desmarca "Encabezados y pies de página"</strong> para que la etiqueta no salga sobre una hoja grande y encaje exacta en el rollo.
              </Alert>
            </Box>

            {/* Columna Derecha: Vista Previa Visual */}
            <Box sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              p: 3,
              bgcolor: alpha(theme.palette.background.default, 0.6),
              borderRadius: 3,
              border: `1px dashed ${theme.palette.divider}`,
              minHeight: 280
            }}>
              <Typography variant="caption" fontWeight="bold" color="text.secondary" sx={{ mb: 2 }}>
                VISTA PREVIA REAL ({currentPreset.widthMm} x {currentPreset.heightMm} mm)
              </Typography>

              {/* Etiqueta renderizada */}
              <Paper
                elevation={3}
                sx={{
                  width: `${Math.min(currentPreset.widthMm * 4.5, 260)}px`,
                  minHeight: `${Math.min(currentPreset.heightMm * 4.5, 170)}px`,
                  p: 1.5,
                  bgcolor: '#ffffff',
                  color: '#000000',
                  border: '1px solid #ddd',
                  borderRadius: 1,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  textAlign: 'center',
                  fontFamily: 'sans-serif',
                  boxShadow: '0 8px 24px rgba(0,0,0,0.12)'
                }}
              >
                {options.showBusinessName && (
                  <Typography sx={{ fontSize: '10px', fontWeight: 900, textTransform: 'uppercase', color: '#000', mb: 0.5 }}>
                    {options.businessName || businessName}
                  </Typography>
                )}

                {options.showName && (
                  <Typography sx={{ fontSize: '13px', fontWeight: 900, lineHeight: 1.1, mb: 0.5, maxWidth: '100%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', textTransform: 'uppercase' }}>
                    {primaryItem.name || primaryItem.nombre || 'Nombre del Producto'}
                  </Typography>
                )}

                {options.showCode && (
                  <Box sx={{ my: 0.5, display: 'flex', flexDirection: 'column', alignItems: 'center', width: '100%', overflow: 'hidden' }}>
                    {codeType === 'qr' ? (
                      // QR codifica POSENT:PROD:<code> para reconocimiento directo por el escáner de cámara del POS
                      <QRCodeSVG
                        value={formatProductQrValue(primaryItem)}
                        size={56}
                        level="Q"
                        includeMargin={false}
                      />
                    ) : (
                      // Código de barras usa el valor plano para compatibilidad con lectores físicos EAN/Code128
                      <Barcode
                        value={String(formatProductCodeValue(primaryItem) || '000000').replace(/[^\x20-\x7E]/g, '') || '000000'}
                        width={1.45}
                        height={38}
                        fontSize={11}
                        displayValue={true}
                        background="#FFFFFF"
                        lineColor="#000000"
                        margin={6}
                      />
                    )}
                    {/* Código de producto legible para encontrar el producto en inventario */}
                    <Typography sx={{ fontSize: '10.5px', fontWeight: 900, fontFamily: 'monospace', color: '#000', mt: 0.4, letterSpacing: '0.05em' }}>
                      CÓD: {formatProductCodeValue(primaryItem)}
                    </Typography>
                  </Box>
                )}

                {options.showCategory && primaryItem.category && (
                  <Typography sx={{ fontSize: '9.5px', fontWeight: 700, color: '#000', mb: 0.5, textTransform: 'uppercase' }}>
                    {primaryItem.category}
                  </Typography>
                )}

                {options.showPrice && (
                  <Typography sx={{ fontSize: '18px', fontWeight: 900, color: '#000', mt: 0.5, letterSpacing: '-0.02em', fontFamily: '"Arial Black", Impact, sans-serif' }}>
                    {formatCurrency(primaryItem.price || 0)}
                  </Typography>
                )}
              </Paper>

              <Chip
                label={`${totalLabels} etiqueta${totalLabels > 1 ? 's' : ''} a imprimir en total`}
                color="primary"
                variant="outlined"
                size="small"
                sx={{ mt: 2, fontWeight: 'bold' }}
              />
            </Box>
          </Box>
        )}

        {tabIndex === 1 && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Alert severity="success" sx={{ borderRadius: 2 }}>
              Este código <strong>ZPL nativo (203 DPI)</strong> está listo para ser enviado directamente a la <strong>Rongta RP425</strong> por puerto serie, USB Raw o importarse en BarTender.
            </Alert>

            <Paper
              sx={{
                p: 2,
                bgcolor: '#1e1e1e',
                color: '#4ec9b0',
                fontFamily: 'monospace',
                fontSize: '12px',
                borderRadius: 2,
                maxHeight: 280,
                overflow: 'auto',
                whiteSpace: 'pre'
              }}
            >
              {generatedZpl}
            </Paper>

            <Stack direction="row" spacing={2} justifyContent="flex-end">
              <Button
                variant="outlined"
                startIcon={copiedZpl ? <CheckIcon /> : <CopyIcon />}
                onClick={handleCopyZpl}
                color={copiedZpl ? 'success' : 'primary'}
              >
                {copiedZpl ? '¡Copiado!' : 'Copiar ZPL'}
              </Button>
              <Button
                variant="outlined"
                startIcon={<DownloadIcon />}
                onClick={handleDownloadZpl}
              >
                Descargar .zpl
              </Button>
            </Stack>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 2.5, borderTop: `1px solid ${theme.palette.divider}`, justifyContent: 'space-between' }}>
        <Button onClick={onClose} variant="outlined" color="inherit">
          Cerrar
        </Button>
        <Button
          variant="contained"
          color="primary"
          size="large"
          startIcon={<PrintIcon />}
          onClick={handlePrint}
          disabled={loading}
          sx={{ px: 4, fontWeight: 'bold', borderRadius: 2 }}
        >
          {loading ? 'Preparando...' : `Imprimir ${totalLabels} Etiqueta${totalLabels > 1 ? 's' : ''}`}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default LabelPreview;